import { ErrorDocument } from '@/types';
import { ErrorItem } from './ErrorItem';
import { Panel, PanelRows } from './Panel';

interface ErrorFeedProps {
  errors: ErrorDocument[];
  loading: boolean;
  /** Why the feed couldn't load, if it couldn't. */
  loadError?: Error | null;
  title?: string;
}

/**
 * The admin-only detail feed.
 *
 * Deliberately a SECOND data source. Service health above comes from the public
 * `serviceStatus` aggregate; this comes from `errors`, which Firestore restricts
 * to admins. Keeping them apart is what lets a signed-out visitor see real
 * health instead of ten "Unknown" rows — and it is why every state below says
 * what it means for THIS panel only, never for the page's verdict.
 */
export function ErrorFeed({ errors, loading, loadError, title = 'Recent Errors' }: ErrorFeedProps) {
  if (loading) {
    return (
      <Panel title={title} action={<Meta>Loading…</Meta>}>
        <PanelRows>
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse px-4 py-3.5 sm:px-5">
              <div className="mb-2 flex items-center gap-2">
                <div className="h-4 w-14 rounded bg-slate-200" />
                <div className="h-3 w-20 rounded bg-slate-100" />
              </div>
              <div className="h-3.5 w-3/4 rounded bg-slate-100" />
            </div>
          ))}
        </PanelRows>
      </Panel>
    );
  }

  // A failed read is NOT good news. Firestore denies `errors` to anyone who
  // isn't a signed-in admin, and rendering the green "no errors" panel on a
  // permission denial is how this page reported all-clear while production was
  // throwing — say what actually happened instead.
  if (loadError) {
    const denied = loadError.message.toLowerCase().includes('permission');
    return (
      <Panel title={title} action={<Meta>Restricted</Meta>}>
        <div className="px-4 py-6 sm:px-5">
          <div className="flex items-start gap-3">
            <LockIcon />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">
                {denied ? 'Sign in to view errors' : 'Unable to load errors'}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {denied
                  ? 'Individual error reports are visible to Someta admins. Use “Sign in” in the header.'
                  : loadError.message}
              </p>
              {/* A locked feed is not an unknown system. Service health above
                  comes from the public aggregate and is unaffected by this. */}
              <p className="mt-2 text-xs text-slate-500">
                Service health above comes from the public feed and is not
                affected by this panel.
              </p>
            </div>
          </div>
        </div>
      </Panel>
    );
  }

  if (errors.length === 0) {
    return (
      <Panel title={title} action={<Meta>None</Meta>}>
        <div className="px-4 py-6 text-sm text-slate-600 sm:px-5">
          No unresolved errors reported in the last 24 hours.
          <span className="mt-1 block text-xs text-slate-500">
            Only errors Someta reported to itself appear here.
          </span>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title={title}
      action={<Meta>{errors.length} unresolved</Meta>}
    >
      <PanelRows>
        {errors.map((error) => (
          <ErrorItem key={error.id} error={error} />
        ))}
      </PanelRows>
    </Panel>
  );
}

function Meta({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-slate-500">{children}</span>;
}

function LockIcon() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 015 0v2" />
    </svg>
  );
}
